
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Service {
  id: string;
  name: string;
  duration_minutes: number;
}

export interface MembershipWithServices {
  id: string;
  provider_id: string;
  name: string;
  description?: string;
  membership_type: 'pack_of_sessions' | 'sessions_per_month' | 'unlimited';
  price: number;
  session_count?: number;
  currency: string;
  is_subscription: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  services: Service[];
}

export const useMemberships = (providerId?: string) => {
  return useQuery({
    queryKey: ['memberships', providerId],
    queryFn: async () => {
      let query = supabase
        .from('memberships')
        .select(`
          *,
          services:membership_services(
            id,
            service:services(
              id,
              name,
              duration_minutes
            )
          )
        `)
        .eq('is_active', true);
      
      if (providerId) {
        query = query.eq('provider_id', providerId);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching memberships:', error);
        throw error;
      }

      // Transform the data to flatten the services structure
      const transformedData = data?.map(membership => ({
        ...membership,
        services: membership.services?.map((ms: any) => ({
          id: ms.service.id,
          name: ms.service.name,
          duration_minutes: ms.service.duration_minutes
        })) || []
      })) || [];

      return transformedData as MembershipWithServices[];
    },
    enabled: !!providerId,
  });
};

export const useCreateMembership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (membershipData: {
      provider_id: string;
      name: string;
      description?: string;
      membership_type: 'pack_of_sessions' | 'sessions_per_month' | 'unlimited';
      price: number;
      session_count?: number;
      service_ids: string[];
      is_subscription: boolean;
    }) => {
      const { service_ids, ...membership } = membershipData;
      
      // Create membership
      const { data: membershipResult, error: membershipError } = await supabase
        .from('memberships')
        .insert(membership)
        .select()
        .single();

      if (membershipError) {
        console.error('Error creating membership:', membershipError);
        throw membershipError;
      }

      // Link services to membership
      if (service_ids.length > 0) {
        const membershipServices = service_ids.map(serviceId => ({
          membership_id: membershipResult.id,
          service_id: serviceId
        }));

        const { error: servicesError } = await supabase
          .from('membership_services')
          .insert(membershipServices);

        if (servicesError) {
          console.error('Error linking services to membership:', servicesError);
          throw servicesError;
        }
      }

      return membershipResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
    },
  });
};

export const useUpdateMembership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      membershipId,
      updates,
      service_ids
    }: {
      membershipId: string;
      updates: Partial<MembershipWithServices>;
      service_ids: string[];
    }) => {
      // Update membership details
      const { error: membershipError } = await supabase
        .from('memberships')
        .update(updates)
        .eq('id', membershipId);

      if (membershipError) {
        console.error('Error updating membership:', membershipError);
        throw membershipError;
      }

      // Update membership services
      // First, remove existing services
      const { error: deleteError } = await supabase
        .from('membership_services')
        .delete()
        .eq('membership_id', membershipId);

      if (deleteError) {
        console.error('Error removing existing membership services:', deleteError);
        throw deleteError;
      }

      // Then, add new services
      if (service_ids.length > 0) {
        const membershipServices = service_ids.map(serviceId => ({
          membership_id: membershipId,
          service_id: serviceId
        }));

        const { error: servicesError } = await supabase
          .from('membership_services')
          .insert(membershipServices);

        if (servicesError) {
          console.error('Error adding new membership services:', servicesError);
          throw servicesError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
    },
  });
};

export const useDeleteMembership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (membershipId: string) => {
      const { error } = await supabase
        .from('memberships')
        .update({ is_active: false })
        .eq('id', membershipId);

      if (error) {
        console.error('Error deleting membership:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
    },
  });
};
